import { TypographyProps } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RowGroupingColumnTableCell } from '../../row-grouping/components/RowGroupingColumnTableCell'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'
import { TextCell } from './TextCell'
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows'

export type TextColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'text'> & {
  columnType: 'text'
  appendText?: string
  textVariant?: TypographyProps['variant']
  color?: TypographyProps['color']
  filterOperators?: GridColDef<T>['filterOperators']
  groupingValueGetter?: GridColDef<T>['groupingValueGetter']
  editable?: boolean
  /** Wraps cell text onto multiple lines instead of truncating. Pair with `getRowHeight={() => 'auto'}` on the DataGrid. */
  wrapText?: boolean
  /** Render group rows via the shared grouping cell (as chip/supplier/custom-render columns do) so the
   * "showing first N of M" truncation note appears; off by default to leave other tables unchanged. */
  renderGroupHeaderCell?: boolean
}

const wrapTextStyle = {
  whiteSpace: 'normal',
  overflow: 'visible',
  textOverflow: 'clip',
  wordBreak: 'break-word',
} as const

export const getTextColumn = <T extends GridValidRowModel>({
  style,
  wrapText,
  renderGroupHeaderCell,
  ...config
}: TextColumnConfig<T>): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  groupingValueGetter: config.groupingValueGetter ?? config.valueGetter,
  renderCell: (params) => {
    if (renderGroupHeaderCell && params.rowNode.type === 'group') {
      // Match chip/custom-render: skip synthetic group rows with no value for this field, so a
      // non-grouped column doesn't paint an empty duplicate header beside the real one.
      if (!isDefined(params.value)) return null
      return <RowGroupingColumnTableCell columnHeaderName={config.headerName ?? ''} value={params.value} id={params.id} />
    }
    return (
      <TextCell
        dataTestId={config.getDataTestId ? config.getDataTestId(params) : undefined}
        variant={config.textVariant}
        color={config.color}
        {...(wrapText ? { style: wrapTextStyle } : {})}
      >
        {params.formattedValue}
        {params.formattedValue && config.appendText ? config.appendText : ''}
      </TextCell>
    )
  },
})
